import React, { useEffect, useState } from 'react';
import axios from 'axios';
import authHeader from '../services/authHeader';
import authService from '../services/authService';
import { useI18n } from '../i18n/I18nProvider';
import Modal from '../components/Modal';

export default function FileManagementPage(){
  async function publishImage(file){
    const st = rowState[file.id];
    if(!st || !st.bin || !st.action) return;
    setError('');
    try {
      const binVal = st.bin === '__ALL__' ? 'ALL' : st.bin;
      await axios.post(`/api/media/${file.id}/publishImage`, { bin: binVal, action: st.action }, { headers: authHeader() });
      setRowState(prev => ({ ...prev, [file.id]: { ...(prev[file.id]||{}), bin:'' } }));
    } catch(e){ setError(e.response?.data?.msg || e.message); }
  }
  const { t } = useI18n();
  const tabStyle = { cursor:'pointer', background:'transparent', border:'none', padding:'8px 14px', fontSize:13, fontWeight:500, color:'#444' };
  const activeTabStyle = { ...tabStyle, borderBottom:'3px solid #1976d2', color:'#000' };
  const [tab, setTab] = useState('audio'); // 'audio' | 'image'
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [selectedBin, setSelectedBin] = useState(''); // legacy single input (kept for image tab only)
  const [bulkBin, setBulkBin] = useState('');
  const [bins, setBins] = useState([]); // for dropdown choices
  const [sites, setSites] = useState([]); // for site dropdown
  const [rowState, setRowState] = useState({}); // { [fileId]: { bin: '', action: '' } }
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, mode: null, payload: null, loading: false });

  const currentUser = authService.getCurrentUser();
  const userId = (() => {
    if (currentUser?.id) return currentUser.id;
    if (currentUser?.token) {
      const decoded = authService.decodeJwt(currentUser.token);
      return decoded?.user?.id || null;
    }
    return currentUser?.email || null;
  })();
  const actionStorageKey = `media-action:${userId || 'anon'}:${tab}`;

  function readActionCache(){
    try {
      const raw = localStorage.getItem(actionStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  function writeActionCache(cache){
    try {
      localStorage.setItem(actionStorageKey, JSON.stringify(cache));
    } catch { /* ignore */ }
  }

  async function load(){
    try {
      const r = await axios.get(`/api/media?type=${tab}`, { headers: authHeader() });
      setFiles(r.data);
    } catch(e){ setError(e.message); }
  }
  useEffect(()=>{ load(); }, [tab]);

  useEffect(() => {
    const cache = readActionCache();
    if (!cache || typeof cache !== 'object') return;
    setRowState(prev => {
      const next = { ...prev };
      Object.entries(cache).forEach(([fileId, action]) => {
        next[fileId] = { ...(next[fileId] || { bin: '', action: '' }), action };
      });
      return next;
    });
  }, [tab]);

  useEffect(()=> {
    // load bins list for dropdowns
    async function fetchBinsAndSites(){
      try {
        const [binsResp, sitesResp] = await Promise.all([
          axios.get('/api/bins', { headers: authHeader() }),
          axios.get('/api/sites', { headers: authHeader() })
        ]);
        // Szűrés: törölt kukák kizárása + 'all' fantom azonosító eltávolítása (külön kezeljük az Összes opcióval)
        setBins(Array.isArray(binsResp.data) ? binsResp.data.filter(b => !b.is_deleted && String(b.bin_identifier).toLowerCase() !== 'all') : []);
        setSites(Array.isArray(sitesResp.data) ? sitesResp.data : []);
      } catch(e){ /* silent */ }
    }
    fetchBinsAndSites();
  }, [tab]);

  async function handleUpload(e){
    const fileList = Array.from(e.target.files || []);
    if(fileList.length === 0) return;
    setUploading(true);
    setError('');
    setUploadProgress({ current: 0, total: fileList.length });
    try {
      const url = tab==='audio' ? '/api/media/upload/audio' : '/api/media/upload/image';
      for(let i = 0; i < fileList.length; i++){
        const f = fileList[i];
        setUploadProgress({ current: i + 1, total: fileList.length });
        const form = new FormData();
        form.append('file', f);
        await axios.post(url, form, { headers: authHeader() });
      }
      await load();
    } catch(e){
      setError(e.response?.data?.msg || e.message);
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      e.target.value='';
    }
  }

  async function assign(id){
    if(!selectedBin) return;
    try {
      await axios.post(`/api/media/${id}/assign`, { bin_id: selectedBin }, { headers: authHeader() });
      await load();
    } catch(e){ setError(e.response?.data?.msg || e.message); }
  }

  const audioActionOptions = [
    { value:'open', label: t('media.action.open') || 'Nyitás' },
    { value:'compression', label: t('media.action.compression') || 'Tömörítés' },
    { value:'full', label: t('media.action.full') || 'Megtelt' },
    { value:'notice', label: t('media.action.notice') || 'Értesítés' }
  ];
  const imageActionOptions = [
    { value:'open', label: t('media.action.open') || 'Nyitás' },
    { value:'compression', label: t('media.action.compression') || 'Tömörítés' },
    { value:'full', label: t('media.action.full') || 'Megtelt' },
    { value:'notice', label: t('media.action.notice') || 'Értesítés' },
    { value:'standby', label: t('media.action.standby') || 'Készenlét' }
  ];

  function updateRow(fileId, patch){
    if (Object.prototype.hasOwnProperty.call(patch, 'action')) {
      const cache = readActionCache();
      if (patch.action) cache[fileId] = patch.action;
      else delete cache[fileId];
      writeActionCache(cache);
    }
    setRowState(prev => ({ ...prev, [fileId]: { ...(prev[fileId]||{ bin:'', action:'' }), ...patch } }));
  }

  async function publish(file){
    const st = rowState[file.id];
    if(!st || !st.bin || !st.action) return;
    setError('');
    try {
      const binVal = st.bin === '__ALL__' ? 'ALL' : st.bin;
      await axios.post(`/api/media/${file.id}/publish`, { bin: binVal, action: st.action }, { headers: authHeader() });
      // Reset bin only, keep action selection for reuse
      setRowState(prev => ({ ...prev, [file.id]: { ...(prev[file.id]||{}), bin:'' } }));
    } catch(e){ setError(e.response?.data?.msg || e.message); }
  }

  async function publishWithBin(file, bin){
    const st = rowState[file.id];
    if(!st || !st.action) return;
    setError('');
    try {
      const binVal = bin === '__ALL__' ? 'ALL' : bin;
      await axios.post(`/api/media/${file.id}/publish`, { bin: binVal, action: st.action }, { headers: authHeader() });
    } catch(e){ setError(e.response?.data?.msg || e.message); }
  }

  async function publishImageWithBin(file, bin){
    const st = rowState[file.id];
    if(!st || !st.action) return;
    setError('');
    try {
      const binVal = bin === '__ALL__' ? 'ALL' : bin;
      await axios.post(`/api/media/${file.id}/publishImage`, { bin: binVal, action: st.action }, { headers: authHeader() });
    } catch(e){ setError(e.response?.data?.msg || e.message); }
  }

  async function doDelete(id, options = {}){
    const { reload = true } = options;
    setDeletingId(id);
    try {
      await axios.delete(`/api/media/${id}`, { headers: authHeader() });
      if (reload) await load();
    } catch(e){ setError(e.response?.data?.msg || e.message); }
    finally { setDeletingId(null); }
  }

  const openConfirm = (mode, payload = null) => {
    setConfirmState({ open: true, mode, payload, loading: false });
  };

  const closeConfirm = () => {
    if (confirmState.loading) return;
    setConfirmState({ open: false, mode: null, payload: null, loading: false });
  };

  const performConfirmed = async () => {
    if (!confirmState.mode) return;
    setConfirmState(cs => ({ ...cs, loading: true }));
    try {
      if (confirmState.mode === 'publish-one') {
        const file = confirmState.payload?.file;
        if (!file) return;
        if (tab === 'audio') await publish(file);
        else await publishImage(file);
      }
      if (confirmState.mode === 'publish-all') {
        if (!bulkBin) return;
        const missing = files.filter(f => !rowState[f.id]?.action);
        if (missing.length > 0) {
          setError(t('media.publishMissingAction') || 'Van olyan fájl, amelyhez nincs kiválasztott művelet.');
          return;
        }
        for (const f of files) {
          if (tab === 'audio') await publishWithBin(f, bulkBin);
          else await publishImageWithBin(f, bulkBin);
        }
        await load();
      }
      if (confirmState.mode === 'delete-one') {
        const id = confirmState.payload?.id;
        if (!id) return;
        await doDelete(id);
      }
      if (confirmState.mode === 'delete-all') {
        for (const f of files) {
          await doDelete(f.id, { reload: false });
        }
        await load();
      }
    } finally {
      setConfirmState({ open: false, mode: null, payload: null, loading: false });
    }
  }

  const allActionsSelected = files.length > 0 && files.every(f => rowState[f.id]?.action);
  const bulkPublishDisabledReason = !bulkBin
    ? (t('media.publishDisabledHint') || 'Válassz kuka és művelet')
    : !allActionsSelected
      ? (t('media.publishMissingAction') || 'Minden fájlnál válassz műveletet')
      : t('media.publish');

  return (
    <div className="page-container" style={{ padding:'12px 16px' }}>
      <div className="tab-buttons" style={{ marginBottom: 0 }}>
        {['audio','image'].map(key => (
          <button
            key={key}
            type="button"
            onClick={()=> setTab(key)}
            className={`btn tab-button ${tab === key ? 'active' : ''}`}
            style={{ fontSize: 13 }}
          >{key === 'audio' ? t('media.audioTab') : t('media.imageTab')}</button>
        ))}
      </div>
      <div style={{ background: '#fff', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '16px 18px', marginTop: 0 }}>
        <div style={{ marginBottom:14, display:'flex', gap:12, flexWrap:'wrap' }}>
        <div>
          <input type="file" accept={tab==='audio'?'.wav':'.bmp'} multiple onChange={handleUpload} disabled={uploading} />
          {uploading && (
            <span style={{ marginLeft:8 }}>
              Feltöltés: {uploadProgress.current}/{uploadProgress.total}
            </span>
          )}
        </div>
        {/* Képek fülön a bin hozzárendelés input eltávolítva, hogy egységes legyen a Hangok füllel */}
        {error && <div style={{ color:'#c62828', fontSize:12 }}>{error}</div>}
      </div>
        <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'#f5f7fa' }}>
              <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.fileName')}</th>
              <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.fileType')}</th>
              <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.version')}</th>
              <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.bin')}</th>
              <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.sizeBytes')}</th>
              {tab === 'audio' && <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.actionColumn') || 'Művelet'}</th>}
              {tab === 'audio' && <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.binColumn') || 'Kuka'}</th>}
              {tab === 'audio' && <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.publish')}</th>}
              {tab === 'image' && <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.actionColumn') || 'Művelet'}</th>}
              {tab === 'image' && <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.binColumn') || 'Kuka'}</th>}
              {tab === 'image' && <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.publish')}</th>}
              <th style={{ textAlign:'left', padding:'6px 8px', borderBottom:'1px solid #ddd' }}>{t('media.delete')||'Törlés'}</th>
            </tr>
          </thead>
          <tbody>
            {files.length > 0 && (
              <tr style={{ background:'#fafbfd' }}>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee', fontWeight:600 }}>
                  {t('media.allFiles') || 'Összes fájl'}
                </td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>-</td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>-</td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>-</td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>-</td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                  {t('media.bulkActionHint') || 'Egyedi műveletek'}
                </td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                  <select
                    value={bulkBin}
                    onChange={e=> setBulkBin(e.target.value)}
                    style={{ fontSize:12, padding:'4px 6px', minWidth:160 }}
                  >
                    <option value="">{t('media.binSelectPlaceholder') || 'Kuka...'}</option>
                    <option value="__ALL__">{t('common.all') || 'Összes'}</option>
                    {sites.length > 0 && <optgroup label={t('media.sitesGroup') || 'Telephelyek'}>
                      {sites.map(site => (
                        <option key={site.site_id} value={String(site.site_id)}>{site.site_code || site.site_id}</option>
                      ))}
                    </optgroup>}
                    {bins.length > 0 && <optgroup label={t('media.binsGroup') || 'Kukák'}>
                      {bins.map(b => (
                        <option key={b.bin_id} value={b.bin_identifier}>{b.bin_identifier}</option>
                      ))}
                    </optgroup>}
                  </select>
                </td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                  <button
                    className="btn btn-sm"
                    disabled={!bulkBin || !allActionsSelected}
                    onClick={()=> openConfirm('publish-all')}
                    title={bulkPublishDisabledReason}
                  >{t('media.publish')}</button>
                </td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={()=> openConfirm('delete-all')}
                    disabled={files.length === 0}
                    title={t('media.deleteHint')||'Végleges törlés'}
                  >{t('media.delete')||'Törlés'}</button>
                </td>
              </tr>
            )}
            {files.map(f => {
              const baseUrl = `/api/media/${f.id}/file`;
              const href = f.file_type === 'audio' ? `${baseUrl}?variant=normalized` : `${baseUrl}?variant=original`;
              return (
              <tr key={f.id}>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                  <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#1976d2' }} title={t('media.open')||'Megnyitás'}>{f.original_filename}</a>
                </td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>{f.file_type}</td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>{f.version}</td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>{f.bin_id || '-'}</td>
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>{f.size_bytes}</td>
                {tab === 'audio' && (
                  <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                    <select
                      value={rowState[f.id]?.action || ''}
                      onChange={e=> updateRow(f.id,{ action: e.target.value })}
                      style={{ fontSize:12, padding:'4px 6px' }}
                    >
                      <option value="">{t('media.actionSelectPlaceholder') || 'Válassz...'}</option>
                      {audioActionOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </td>
                )}
                {tab === 'audio' && (
                  <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                    <select
                      value={rowState[f.id]?.bin || ''}
                      onChange={e=> updateRow(f.id,{ bin: e.target.value })}
                      style={{ fontSize:12, padding:'4px 6px', minWidth:160 }}
                    >
                      <option value="">{t('media.binSelectPlaceholder') || 'Kuka...'}</option>
                      <option value="__ALL__">{t('common.all') || 'Összes'}</option>
                      {/* Telephelyek szekció */}
                      {sites.length > 0 && <optgroup label={t('media.sitesGroup') || 'Telephelyek'}>
                        {sites.map(site => (
                          <option key={site.site_id} value={String(site.site_id)}>{site.site_code || site.site_id}</option>
                        ))}
                      </optgroup>}
                      {/* Kukák szekció */}
                      {bins.length > 0 && <optgroup label={t('media.binsGroup') || 'Kukák'}>
                        {bins.map(b => (
                          <option key={b.bin_id} value={b.bin_identifier}>{b.bin_identifier}</option>
                        ))}
                      </optgroup>}
                    </select>
                  </td>
                )}
                {tab === 'audio' && (
                  <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                    <button
                      className="btn btn-sm"
                      disabled={!(rowState[f.id]?.bin && rowState[f.id]?.action)}
                      onClick={()=> openConfirm('publish-one', { file: f })}
                      title={!(rowState[f.id]?.bin && rowState[f.id]?.action) ? (t('media.publishDisabledHint')||'Válassz kuka és művelet') : t('media.publish')}
                    >{t('media.publish')}</button>
                  </td>
                )}
                {tab === 'image' && (
                  <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                    <select
                      value={rowState[f.id]?.action || ''}
                      onChange={e=> updateRow(f.id,{ action: e.target.value })}
                      style={{ fontSize:12, padding:'4px 6px' }}
                    >
                      <option value="">{t('media.actionSelectPlaceholder') || 'Válassz...'}</option>
                      {imageActionOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </td>
                )}
                {tab === 'image' && (
                  <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                    <select
                      value={rowState[f.id]?.bin || ''}
                      onChange={e=> updateRow(f.id,{ bin: e.target.value })}
                      style={{ fontSize:12, padding:'4px 6px', minWidth:160 }}
                    >
                      <option value="">{t('media.binSelectPlaceholder') || 'Kuka...'}</option>
                      <option value="__ALL__">{t('common.all') || 'Összes'}</option>
                      {/* Telephelyek szekció */}
                      {sites.length > 0 && <optgroup label={t('media.sitesGroup') || 'Telephelyek'}>
                        {sites.map(site => (
                          <option key={site.site_id} value={String(site.site_id)}>{site.site_code || site.site_id}</option>
                        ))}
                      </optgroup>}
                      {/* Kukák szekció */}
                      {bins.length > 0 && <optgroup label={t('media.binsGroup') || 'Kukák'}>
                        {bins.map(b => (
                          <option key={b.bin_id} value={b.bin_identifier}>{b.bin_identifier}</option>
                        ))}
                      </optgroup>}
                    </select>
                  </td>
                )}
                {tab === 'image' && (
                  <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                    <button
                      className="btn btn-sm"
                      disabled={!(rowState[f.id]?.bin && rowState[f.id]?.action)}
                      onClick={()=> openConfirm('publish-one', { file: f })}
                      title={!(rowState[f.id]?.bin && rowState[f.id]?.action) ? (t('media.publishDisabledHint')||'Válassz kuka és művelet') : t('media.publish')}
                    >{t('media.publish')}</button>
                  </td>
                )}
                <td style={{ padding:'6px 8px', borderBottom:'1px solid #eee' }}>
                  <button className="btn btn-sm btn-danger" onClick={()=> openConfirm('delete-one', { id: f.id })} disabled={deletingId===f.id} title={t('media.deleteHint')||'Végleges törlés'}>
                    {deletingId===f.id ? (t('media.deleting')||'Törlés...') : (t('media.delete')||'Törlés')}
                  </button>
                </td>
              </tr>
            )})}
            {files.length === 0 && (
              <tr><td colSpan={9} style={{ padding:'10px 8px', textAlign:'center', fontSize:12, opacity:.7 }}>{t('media.noFiles')}</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
      <Modal
        open={confirmState.open}
        title={t('media.confirmTitle') || 'Megerősítés'}
        onClose={closeConfirm}
        actions={
          <>
            <button className="btn" onClick={closeConfirm} disabled={confirmState.loading}>
              {t('common.cancel') || 'Mégse'}
            </button>
            <button className="btn btn-danger" onClick={performConfirmed} disabled={confirmState.loading} data-autofocus>
              {t('common.confirm') || 'Igen'}
            </button>
          </>
        }
      >
        {confirmState.mode === 'publish-one' && (t('media.confirmPublish') || 'Biztosan publikálod a kiválasztott fájlt?')}
        {confirmState.mode === 'publish-all' && (t('media.confirmPublishAll') || 'Biztosan publikálod az összes fájlt?')}
        {confirmState.mode === 'delete-one' && (t('media.deleteConfirm') || 'Biztosan törlöd?')}
        {confirmState.mode === 'delete-all' && (t('media.deleteAllConfirm') || 'Biztosan törlöd az összes fájlt?')}
      </Modal>
    </div>
  );
}